import {
  GraduationCap,
  LayoutDashboard,
  MessagesSquare,
  Settings,
  SlidersHorizontal,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { LiksPagination } from '../../components/liks/LiksPagination/LiksPagination';
import { LiksPage } from '../../components/liks/LiksPage/LiksPage';
import { LiksPageHeading } from '../../components/liks/LiksPageHeading/LiksPageHeading';
import { LiksTabs } from '../../components/liks/LiksTabs/LiksTabs';
import {
  canEditChatbotConfiguration,
  canManageChatbotSettings,
  canManageChatbotTraining,
} from '../../features/chatbots/chatbotPermissions';
import {
  DEFAULT_CHATBOT_TAB,
  isChatbotTabId,
  resolveChatbotTabPath,
  type ChatbotTabId,
} from '../../features/chatbots/chatbotTabs';
import { useChatbotConversationsTab } from '../../features/chatbots/hooks/useChatbotConversationsTab';
import { useChatbotTrainingTab } from '../../features/chatbots/hooks/useChatbotTrainingTab';
import { useCurrentChatbotRole } from '../../features/chatbots/hooks/useCurrentChatbotRole';
import { useChatbotMembersQuery } from '../../features/chatbots/queries/useChatbotMembersQuery';
import { useChatbotQuery } from '../../features/chatbots/queries/useChatbotQuery';
import { useI18n } from '../../i18n/useI18n';
import {
  CHATBOT_CONVERSATIONS_PAGE_SIZES,
  CHATBOT_TRAINING_EXAMPLES_PAGE_SIZES,
} from '../../types/userConfig';
import { ChatbotEmbedPreview } from './ChatbotEmbedPreview';
import { ChatbotConfigurationTab } from './tabs/ChatbotConfigurationTab';
import { ChatbotConversationsTab } from './tabs/ChatbotConversationsTab';
import { ChatbotMembersTab } from './tabs/ChatbotMembersTab';
import { ChatbotOverviewTab } from './tabs/ChatbotOverviewTab';
import { ChatbotSettingsTab } from './tabs/ChatbotSettingsTab';
import { ChatbotTrainingTab } from './tabs/ChatbotTrainingTab';
import './ChatbotPage.css';

export function ChatbotPage() {
  const { chatbotId, tab } = useParams<{ chatbotId: string; tab?: string }>();
  const navigate = useNavigate();
  const { translate } = useI18n();
  const [isEmbedPreviewEnabled, setIsEmbedPreviewEnabled] = useState(true);
  const resolvedChatbotId = chatbotId?.trim() ?? '';
  const activeTabId: ChatbotTabId | null = isChatbotTabId(tab) ? tab : null;
  const isConfigurationTab = activeTabId === 'configuration';
  const isConversationsTab = activeTabId === 'conversations';
  const isTrainingTab = activeTabId === 'training';
  const chatbotQuery = useChatbotQuery(
    resolvedChatbotId !== '' ? resolvedChatbotId : undefined,
  );
  const membersQuery = useChatbotMembersQuery(
    resolvedChatbotId !== '' ? resolvedChatbotId : undefined,
  );
  const currentChatbotRole = useCurrentChatbotRole(
    resolvedChatbotId !== '' ? resolvedChatbotId : undefined,
  );
  const canEditConfiguration = canEditChatbotConfiguration(currentChatbotRole);
  const canManageSettings = canManageChatbotSettings(currentChatbotRole);
  const canManageTraining = canManageChatbotTraining(currentChatbotRole);
  const conversationsTab = useChatbotConversationsTab(
    resolvedChatbotId,
    isConversationsTab,
  );
  const trainingTab = useChatbotTrainingTab(resolvedChatbotId, isTrainingTab);

  if (resolvedChatbotId === '') {
    return <Navigate to="/chatbots" replace />;
  }

  if (activeTabId === null) {
    return (
      <Navigate
        to={resolveChatbotTabPath(resolvedChatbotId, DEFAULT_CHATBOT_TAB)}
        replace
      />
    );
  }

  if (chatbotQuery.isLoading || membersQuery.isLoading) {
    return <LiksPage content={<p>{translate('chatbots.loading')}</p>} />;
  }

  if (
    (activeTabId === 'configuration' && !canEditConfiguration) ||
    (activeTabId === 'settings' && !canManageSettings)
  ) {
    return (
      <Navigate
        to={resolveChatbotTabPath(resolvedChatbotId, DEFAULT_CHATBOT_TAB)}
        replace
      />
    );
  }

  if (chatbotQuery.isError) {
    return (
      <LiksPage
        content={
          <p role="alert">
            {chatbotQuery.error instanceof Error
              ? chatbotQuery.error.message
              : translate('chatbots.loadError')}
          </p>
        }
      />
    );
  }

  const chatbot = chatbotQuery.data;
  if (!chatbot) {
    return <Navigate to="/chatbots" replace />;
  }

  const chatbotName = chatbot.name;
  const members = membersQuery.data ?? [];

  const handleTabChange = (nextTabId: string) => {
    if (!isChatbotTabId(nextTabId)) {
      return;
    }
    if (nextTabId === 'configuration' && !canEditConfiguration) {
      return;
    }
    if (nextTabId === 'settings' && !canManageSettings) {
      return;
    }
    if (nextTabId === 'configuration') {
      setIsEmbedPreviewEnabled(true);
    } else {
      setIsEmbedPreviewEnabled(false);
    }
    navigate(resolveChatbotTabPath(resolvedChatbotId, nextTabId));
  };

  const paginationTab = isConversationsTab
    ? {
        pageSize: conversationsTab.chatbotConversationsPageSize,
        pageSizes: CHATBOT_CONVERSATIONS_PAGE_SIZES,
        page: conversationsTab.page,
        hasPreviousPage: conversationsTab.hasPreviousPage,
        hasNextPage: conversationsTab.hasNextPage,
        onPageSizeChange: conversationsTab.handlePageSizeChange,
        onPreviousPage: () =>
          conversationsTab.setPage(Math.max(1, conversationsTab.page - 1)),
        onNextPage: () => {
          if (conversationsTab.hasNextPage) {
            conversationsTab.setPage(conversationsTab.page + 1);
          }
        },
      }
    : isTrainingTab
      ? {
          pageSize: trainingTab.chatbotTrainingExamplesPageSize,
          pageSizes: CHATBOT_TRAINING_EXAMPLES_PAGE_SIZES,
          page: trainingTab.page,
          hasPreviousPage: trainingTab.hasPreviousPage,
          hasNextPage: trainingTab.hasNextPage,
          onPageSizeChange: trainingTab.handlePageSizeChange,
          onPreviousPage: () =>
            trainingTab.setPage(Math.max(1, trainingTab.page - 1)),
          onNextPage: () => {
            if (trainingTab.hasNextPage) {
              trainingTab.setPage(trainingTab.page + 1);
            }
          },
        }
      : null;

  const tabs = [
    {
      id: 'overview',
      label: translate('chatbots.tabs.overview'),
      icon: <LayoutDashboard aria-hidden />,
      content: <ChatbotOverviewTab chatbot={chatbot} />,
    },
    {
      id: 'conversations',
      label: translate('chatbots.tabs.conversations'),
      icon: <MessagesSquare aria-hidden />,
      content: (
        <ChatbotConversationsTab
          chatbotId={chatbot.id}
          conversationsTab={conversationsTab}
        />
      ),
    },
    {
      id: 'training',
      label: translate('chatbots.tabs.training'),
      icon: <GraduationCap aria-hidden />,
      content: (
        <ChatbotTrainingTab
          chatbotId={chatbot.id}
          chatbotName={chatbot.name}
          trainingTab={trainingTab}
          canManageTraining={canManageTraining}
        />
      ),
    },
    {
      id: 'members',
      label: translate('chatbots.tabs.members'),
      icon: <Users aria-hidden />,
      content: (
        <ChatbotMembersTab
          chatbotId={chatbot.id}
          chatbotName={chatbotName}
          members={members}
        />
      ),
    },
    ...(canEditConfiguration
      ? [
          {
            id: 'configuration',
            label: translate('chatbots.tabs.configuration'),
            icon: <SlidersHorizontal aria-hidden />,
            content: (
              <ChatbotConfigurationTab
                key={chatbot.id}
                chatbot={chatbot}
                showPreview={isEmbedPreviewEnabled}
                onShowPreviewChange={setIsEmbedPreviewEnabled}
              />
            ),
          },
        ]
      : []),
    ...(canManageSettings
      ? [
          {
            id: 'settings',
            label: translate('chatbots.tabs.settings'),
            icon: <Settings aria-hidden />,
            content: <ChatbotSettingsTab chatbot={chatbot} />,
          },
        ]
      : []),
  ];

  return (
    <LiksPage
      className="chatbot-page"
      header={
        <LiksPageHeading
          aria-label={translate('chatbots.breadcrumb.legend')}
          items={[
            { label: translate('chatbots.title'), to: '/chatbots' },
            { label: chatbotName },
          ]}
        />
      }
      content={
        <div className="chatbot-page__content">
          <LiksTabs
            tabs={tabs}
            activeTabId={activeTabId}
            onActiveTabIdChange={handleTabChange}
            aria-label={translate('chatbots.tabs.legend')}
          />
          {isConfigurationTab && isEmbedPreviewEnabled && (
            <ChatbotEmbedPreview chatbot={chatbot} showStatus={false} />
          )}
        </div>
      }
      footer={
        paginationTab ? (
          <div className="chatbot-page__footer">
            <LiksPagination
              page={paginationTab.page}
              pageSize={paginationTab.pageSize}
              pageSizes={paginationTab.pageSizes}
              onPageSizeChange={(size) =>
                paginationTab.onPageSizeChange(String(size))
              }
              hasPreviousPage={paginationTab.hasPreviousPage}
              hasNextPage={paginationTab.hasNextPage}
              onPreviousPage={paginationTab.onPreviousPage}
              onNextPage={paginationTab.onNextPage}
            />
          </div>
        ) : undefined
      }
    />
  );
}
